import React, {useState,useEffect,useRef} from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from '@aws-amplify/auth';
import { toast } from "react-toastify";
import { SettingsAPI, ClinicProfile, isDoctorProfile } from "../../../services/settings.service";
import { DoctorKycAPI, KycDocument, KycDocType } from "../../../services/doctorKyc.service";
import { SettingsCard } from "../SettingsCard";
import { EditableCardFooter } from "../EditableCardFooter";
import { ImageActionsModal } from "./ImageActionsModal";
import swasthiqSymbol from "../../../assets/kaagazy-symbol.png";
import {
    DocTile,
    DocTileInfo,
    DocState,
    ACCEPTED_TYPES,
    MAX_FILE_SIZE,
    formatDate,
    toDocStatus,
    putToS3,
} from "../../../components/settings/DocTile";

// KYC document tiles shown in the card, in display order. Uses the same
// DocTile component (and upload/view UX) as the Clinic Credentialing card.
const KYC_DOC_TILES: DocTileInfo[] = [
    { id: "aadhar",               label: "Aadhar Card",          description: "Your Aadhar card" },
    { id: "pan",                  label: "PAN Card",             description: "Your PAN card" },
    { id: "medical_registration", label: "Medical Registration", description: "Your NMC medical registration certificate" },
    { id: "degree",               label: "Degree Certificate",   description: "Your medical degree certificate" },
];

const PENDING_KYC_STATE: Record<string, DocState> = Object.fromEntries(
    KYC_DOC_TILES.map((d): [string, DocState] => [d.id, { status: "pending" }])
);

function mapKycDoc(m: KycDocument): DocState {
    return {
        status: toDocStatus(m.status),
        uploadedAt: formatDate(m.updated_at ?? m.created_at),
        docId: m.kyc_doc_id,
        rejectionReason: m.rejection_reason ?? undefined,
        viewUrl: null, // fetched on demand via a short-lived presigned GET
    };
}

function buildKycState(kycDocs: KycDocument[]): Record<string, DocState> {
    const next: Record<string, DocState> = { ...PENDING_KYC_STATE };
    for (const doc of kycDocs) {
        if (next[doc.document_type] !== undefined) next[doc.document_type] = mapKycDoc(doc);
    }
    return next;
}

interface Props{
    /** Notify the page when the profile changes so the header avatar/name stay in sync. */
    onProfileChange?: (profile: ClinicProfile) => void;
}


export const ProfileAccount: React.FC<Props>=({
    onProfileChange,
})=>{
    const navigate=useNavigate();


    const [profile,setProfile]=useState<ClinicProfile | null>(null);
    const [docs, setDocs] = useState<Record<string, DocState>>(PENDING_KYC_STATE);
    const [loading,setLoading]=useState(true);

    // Single full_name field matches Figma; split to first/last on save
    const [form, setForm] = useState({
        full_name:   "",
        designation: "",
        phone:       "",
    });
    const [saving, setSaving] = useState(false);
    // Fields stay locked until the user explicitly clicks Edit — guards against
    // accidental edits/saves of profile info.
    const [editingProfile, setEditingProfile] = useState(false);

    const photoInputRef = useRef<HTMLInputElement>(null);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [deletingPhoto, setDeletingPhoto] = useState(false);
    const [showPhotoModal, setShowPhotoModal] = useState(false);

    useEffect(()=>{
        SettingsAPI.getProfile().then(p=>{
            setProfile(p);
            setForm({
                full_name:   `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim(),
                designation: p.designation ?? "",
                phone:       p.phone ?? "",
            });
            // KYC is doctor data; only a DoctorProfile carries a doctor_id.
            if (isDoctorProfile(p)) {
                return DoctorKycAPI.list(p.doctor_id)
                    .then(list => setDocs(buildKycState(list)))
                    .catch(()=>{ /* non-fatal: card still renders, just empty */ });
            }
        }).catch(()=>{
            toast.error("Failed to load profile");
        }).finally(()=>{
            setLoading(false);
        });
    },[]);

    const handleSaveProfile = async () => {
        // Split "Dr. Sarah Johnson" → first_name="Dr. Sarah"  last_name="Johnson"
        const parts      = form.full_name.trim().split(/\s+/);
        const first_name = parts[0] ?? "";
        const last_name  = parts.slice(1).join(" ");
        setSaving(true);
        try {
            const updated = await SettingsAPI.saveProfile({ first_name, last_name, designation: form.designation, phone: form.phone });
            setProfile(updated);
            onProfileChange?.(updated);
            setEditingProfile(false);
            toast.success("Profile saved");
        } catch {
            toast.error("Failed to save profile");
        } finally {
            setSaving(false);
        }
    };

    // Discard any unsaved edits and re-lock the fields.
    const handleCancelEditProfile = () => {
        if (profile) {
            setForm({
                full_name:   `${profile.first_name ?? ""} ${profile.last_name ?? ""}`.trim(),
                designation: profile.designation ?? "",
                phone:       profile.phone ?? "",
            });
        }
        setEditingProfile(false);
    };
    
    const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";   // allow re-selecting the same file
        if (!file) return;
        if (!["image/jpeg", "image/png"].includes(file.type)) {
            toast.error("Please choose a JPG or PNG image");
            return;
        }
        setUploadingPhoto(true);
        try {
            const updated = await SettingsAPI.uploadProfilePhoto(file);
            setProfile(updated);
            onProfileChange?.(updated);
            toast.success("Photo updated");
        } catch {
            toast.error("Failed to upload photo");
        } finally {
            setUploadingPhoto(false);
        }
    };

    const handleDeletePhoto = async () => {
        setDeletingPhoto(true);
        try {
            const updated = await SettingsAPI.deleteProfilePhoto();
            setProfile(updated);
            onProfileChange?.(updated);
            toast.success("Photo removed");
        } catch {
            toast.error("Failed to remove photo");
        } finally {
            setDeletingPhoto(false);
        }
    };

    // Modal actions: "Upload" hands off to the hidden file input; "Delete"
    // removes the photo, then both close the modal.
    const handleModalUpload = () => {
        setShowPhotoModal(false);
        photoInputRef.current?.click();
    };

    const handleModalDelete = async () => {
        await handleDeletePhoto();
        setShowPhotoModal(false);
    };

    // Per-tile upload — mirrors Clinic Credentialing's handleFileSelect so both
    // cards behave identically: pick/drop a file on a tile, it uploads right away.
    const handleKycFileSelect = async (id: string, file: File) => {
        if (!ACCEPTED_TYPES.includes(file.type)) {
            toast.error("Only PDF, JPG or PNG files are accepted.");
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            toast.error("File size must be under 5 MB.");
            return;
        }
        const doctorId = profile && isDoctorProfile(profile) ? profile.doctor_id : null;
        if (!doctorId) return;
        const type = id as KycDocType;
        const label = KYC_DOC_TILES.find(d => d.id === id)?.label ?? "Document";
        if (docs[id]?.status === "verified") {
            if (!window.confirm(`Replace the verified "${label}"? The new file will be sent back for review.`)) return;
        }

        setDocs(prev => ({ ...prev, [id]: { ...prev[id], status: "submitted", fileName: file.name, uploading: true } }));
        try {
            const presigned = await DoctorKycAPI.presign(doctorId, [type]);
            const entry = presigned.entries.find(e => e.document_type === type);
            if (!entry) throw new Error("No presigned URL for this document type.");
            if (entry.upload_url) await putToS3(entry.upload_url, file);
            // confirm() returns the refreshed current set, so no extra list() call.
            const updated = await DoctorKycAPI.confirm(doctorId, [{ document_type: type, s3_key: entry.s3_key }]);
            setDocs(buildKycState(updated));
            toast.success(`${label} uploaded — pending review`);
        } catch {
            toast.error(`Failed to upload ${label}. Please try again.`);
            setDocs(prev => ({ ...prev, [id]: { ...prev[id], uploading: false, fileName: undefined, status: prev[id].docId ? prev[id].status : "pending" } }));
        }
    };

    // Open a stored KYC document via a short-lived presigned GET (private bucket).
    const handleKycView = async (id: string) => {
        const doctorId = profile && isDoctorProfile(profile) ? profile.doctor_id : null;
        const docId = docs[id]?.docId;
        if (!doctorId || !docId) return;
        try {
            const { view_url } = await DoctorKycAPI.viewUrl(doctorId, docId);
            window.open(view_url, "_blank", "noopener,noreferrer");
        } catch {
            toast.error("Could not open document");
        }
    };

    const handleLogOut = async () => {
        await signOut();
        navigate("/login", { replace: true });
    };

    if (loading) {
        return <div className="text-sm text-gray-400 py-10 text-center">Loading profile…</div>;
    }

    // Only doctors carry a doctor_id; non-doctors don't see the KYC card at all.
    const doctorId = profile && isDoctorProfile(profile) ? profile.doctor_id : null;
    const submittedKycCount = KYC_DOC_TILES.filter(d => docs[d.id]?.status !== "pending").length;

    // Shared input class
    const inputCls =
        "w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";
    // Locked appearance when the profile card is not in edit mode
    const lockedCls =
        "w-full border border-gray-100 rounded-lg px-3 py-2 text-base sm:text-sm text-gray-700 bg-gray-50 cursor-not-allowed";
    const profileFieldCls = editingProfile ? inputCls : lockedCls;

    return (
        <div className="space-y-5">

            {/* ── Card 1: My Profile ──────────────────────────────────────── */}
            <SettingsCard
                title="My Profile"
                icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                    </svg>
                }
                footer={
                    <EditableCardFooter
                        editing={editingProfile}
                        saving={saving}
                        onEdit={() => setEditingProfile(true)}
                        onSave={handleSaveProfile}
                        onCancel={handleCancelEditProfile}
                    />
                }
            >
                <div className="flex items-start gap-5">

                    {/* Avatar — teal outlined circle matching Figma */}
                    <div className="relative flex-shrink-0">
                        <div className="w-24 h-24 rounded-full border-2 border-cyan-400 bg-white flex items-center justify-center select-none overflow-hidden">
                            {/* User photo if uploaded, else the SwasthiQ logo (no initials). */}
                            <img
                                src={profile?.photo_url || swasthiqSymbol}
                                alt="Profile"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <input
                            ref={photoInputRef}
                            type="file"
                            accept="image/jpeg,image/png"
                            onChange={handlePhotoChange}
                            className="hidden"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPhotoModal(true)}
                            disabled={uploadingPhoto || deletingPhoto}
                            className="absolute bottom-0 right-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white shadow-sm hover:bg-blue-600 disabled:opacity-50"
                            title="Change photo"
                        >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                                <circle cx="12" cy="13" r="4"/>
                            </svg>
                        </button>
                    </div>

                    {/* Fields — 2-column grid matching Figma exactly */}
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 min-w-0">

                        {/* Row 1 */}
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Full Name</label>
                            <input
                                className={profileFieldCls}
                                value={form.full_name}
                                onChange={e => setForm({ ...form, full_name: e.target.value })}
                                placeholder="Dr. Sarah Johnson"
                                disabled={!editingProfile}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Designation</label>
                            <input
                                className={profileFieldCls}
                                value={form.designation}
                                onChange={e => setForm({ ...form, designation: e.target.value })}
                                disabled={!editingProfile}
                            />
                        </div>

                        {/* Row 2 */}
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                            <input
                                className="w-full border border-gray-100 rounded-lg px-3 py-2 text-sm text-gray-400 bg-gray-50 cursor-not-allowed"
                                value={profile?.email ?? ""}
                                disabled
                                title="To change your registered email, contact support."
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Phone</label>
                            <input
                                className={profileFieldCls}
                                value={form.phone}
                                onChange={e => setForm({ ...form, phone: e.target.value })}
                                placeholder="+91 98765 43210"
                                disabled={!editingProfile}
                            />
                        </div>

                    </div>
                </div>
            </SettingsCard>

            {/* ── Card 2: Doctor KYC (doctors only) ────────────────────────── */}
            {/* Same DocTile upload/view UX as the Clinic Credentialing card:
                each tile uploads (or replaces) its own file immediately. */}
            {doctorId && (
            <SettingsCard
                title="Doctor KYC"
                subtitle="Complete your verification documents"
                icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                        <polyline points="10 9 9 9 8 9"/>
                    </svg>
                }
                footer={
                    <p className="text-xs text-slate-500">
                        {submittedKycCount} of {KYC_DOC_TILES.length} documents submitted · Each upload is sent for SwasthiQ review automatically
                    </p>
                }
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {KYC_DOC_TILES.map(d => (
                        <DocTile
                            key={d.id}
                            doc={d}
                            state={docs[d.id]}
                            onFileSelect={handleKycFileSelect}
                            onView={handleKycView}
                        />
                    ))}
                </div>
            </SettingsCard>
            )}

            {/* ── Log Out ──────────────────────────────────────────────────── */}
            <div className="flex justify-start">
                <button
                    onClick={handleLogOut}
                    className="px-5 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                >
                    Log Out
                </button>
            </div>

            {/* Profile photo — upload / delete */}
            <ImageActionsModal
                isOpen={showPhotoModal}
                onClose={() => setShowPhotoModal(false)}
                title="Profile Photo"
                subtitle="Upload a new photo or remove the current one"
                noun="photo"
                hasImage={!!profile?.photo_url}
                uploading={uploadingPhoto}
                deleting={deletingPhoto}
                onUploadClick={handleModalUpload}
                onDelete={handleModalDelete}
            />

        </div>
    );
};